/**
 * Other Documents — "وثائق أخرى" (Feb 2026 / iter-181).
 *
 * INSERT-ONLY vault (iter-262). The page shows the upload card exclusively.
 * The saved documents are rendered inside the company file itself (search
 * lives there too), so this page is a pure "insert" surface.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Upload, Sparkles, Loader2, Save, FileArchive, Building2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { AgentPageShell } from './agent/AgentPageShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const [prefix, base64] = String(reader.result).split(',');
      const m = /^data:([^;]+);base64$/.exec(prefix);
      resolve({ base64, mime: m ? m[1] : file.type });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const AgentOtherDocsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [searchParams] = useSearchParams();
  const preselectedCompanyId = searchParams.get('company_id') || '';

  // iter-262 — pure insert surface. No more docs list on this page.
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(preselectedCompanyId);

  // Upload form state
  const [file, setFile] = useState(null);
  const [titleAr, setTitleAr] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  // iter-262 — Route back to the parent company file when we came from one
  // via `?company_id=…`. Otherwise fall back to the hub.
  const backTarget = preselectedCompanyId
    ? `/manage/cloudgm-agent/companies/${preselectedCompanyId}`
    : '/manage/cloudgm-agent';

  const breadcrumb = preselectedCompanyId
    ? [
        { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
        { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
        { label_ar: 'ملفات الشركات', label_en: 'Company Files', path: '/manage/cloudgm-agent/companies' },
        { label_ar: 'ملف الشركة', label_en: 'Company File', path: backTarget },
        { label_ar: 'إضافة وثائق أخرى', label_en: 'Add Other Documents', path: null },
      ]
    : [
        { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
        { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
        { label_ar: 'وثائق أخرى', label_en: 'Other Documents', path: null },
      ];

  // Load companies (only needed if the operator hasn't picked one via URL).
  const loadCompanies = async () => {
    try {
      const coR = await axios.get(`${API}/agent/companies`, { headers: getAuthHeaders() });
      setCompanies(coR.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  useEffect(() => { loadCompanies(); }, []);

  const handleFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error(isAr ? 'الحجم الأقصى 10 ميغا' : 'Max 10 MB');
      return;
    }
    const { base64, mime } = await readFileAsBase64(f);
    setFile({ base64, mime, name: f.name });
    // reset titles so AI can propose new ones for this file
    setTitleAr('');
    setTitleEn('');
  };

  const runAIExtract = async () => {
    if (!file) {
      toast.error(isAr ? 'ارفع الملف أولاً' : 'Upload the file first');
      return;
    }
    setExtracting(true);
    try {
      const res = await axios.post(
        `${API}/agent/other-docs/extract-title`,
        { file_base64: file.base64, mime: file.mime },
        { headers: getAuthHeaders(), timeout: 60_000 }
      );
      const d = res.data?.data || {};
      const ar = d.title_ar || '';
      const en = d.title_en || '';
      setTitleAr(ar);
      setTitleEn(en);
      if (ar || en) toast.success(isAr ? 'تم استخراج العنوان بالعربي والإنجليزي' : 'Extracted AR + EN titles');
      else toast.error(isAr ? 'تعذّر استخراج عنوان — اكتبه يدوياً' : 'No title extracted — please type one');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setExtracting(false);
    }
  };

  // iter-260 — Fill the missing language via AI translation.
  const autoTranslate = async (direction) => {
    // direction: 'ar_to_en' | 'en_to_ar'
    const isArToEn = direction === 'ar_to_en';
    const source = isArToEn ? titleAr.trim() : titleEn.trim();
    if (!source) {
      toast.error(isAr ? 'اكتب العنوان أولاً' : 'Type the title first');
      return;
    }
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/utils/translate`, {
        text: source,
        source: isArToEn ? 'ar' : 'en',
        target: isArToEn ? 'en' : 'ar',
      }, { headers: getAuthHeaders() });
      const translated = res.data?.translated || '';
      if (!translated) throw new Error('empty');
      if (isArToEn) setTitleEn(translated);
      else setTitleAr(translated);
      toast.success(isAr ? 'تمت الترجمة' : 'Translated');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    if (!companyId) {
      toast.error(isAr ? 'اختر الشركة أولاً — الوثيقة تُدرج تحت ملف شركة' : 'Pick a company — documents live under a company file');
      return;
    }
    const ar = titleAr.trim();
    const en = titleEn.trim();
    if (!ar && !en) {
      toast.error(isAr ? 'أدخل العنوان بالعربي أو الإنجليزي' : 'Enter title in Arabic or English');
      return;
    }
    if (!file) {
      toast.error(isAr ? 'ارفع ملفاً' : 'Upload a file');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/agent/other-docs`, {
        company_id: companyId,
        title: ar || en,
        title_ar: ar,
        title_en: en,
        document_file: file.base64,
        document_file_mime: file.mime,
        document_file_name: file.name,
      }, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم الحفظ' : 'Saved');
      // iter-262 — On save, route back to the parent company file so the
      // operator SEES the new doc rendered inside the "Other Documents"
      // section there (with its own search box).
      navigate(backTarget);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AgentPageShell
      breadcrumb={breadcrumb}
      maxWidth="max-w-5xl"
      onBack={() => navigate(backTarget)}
    >
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="agent-other-docs-title">
          <FileArchive className="h-6 w-6 text-yellow-400" />
          {isAr ? 'وثائق أخرى' : 'Other Documents'}
        </h1>
      </div>

      {/* Upload + AI title */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6"
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-yellow-400" />
          <h2 className="font-semibold text-white">
            {isAr ? 'رفع وثيقة (يقرأ الذكاء الاصطناعي عنوانها فقط)' : 'Upload document (AI extracts title only)'}
          </h2>
        </div>

        {/* iter-244 — hide company picker when opened from inside a
           company file (URL `?company_id=...`). Show a compact header pill
           instead so the operator always knows which file they're inside. */}
        {preselectedCompanyId ? (
          <div className="mb-4 flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3"
               data-testid="agent-other-preselected-company">
            <Building2 className="h-4 w-4 text-emerald-300" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-emerald-300 uppercase tracking-wide">{isAr ? 'ملف الشركة' : 'Company file'}</div>
              <div className="text-white text-sm font-semibold truncate">
                {(() => {
                  const c = companies.find((x) => x.id === companyId);
                  if (!c) return isAr ? 'جاري التحميل…' : 'Loading…';
                  return (isAr ? (c.company_name_ar || c.company_name_en) : (c.company_name_en || c.company_name_ar))
                    + (c.license_number ? ` — ${c.license_number}` : '');
                })()}
              </div>
            </div>
          </div>
        ) : (
        <div className="mb-4">
          <Label className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {isAr ? 'اختر ملف الشركة (إلزامي)' : 'Select company file (required)'}</Label>
          {companies.length === 0 ? (
            <p className="text-sm text-amber-400 mt-2">
              {isAr ? 'لا توجد شركات — أنشئ ملف شركة أولاً.' : 'No companies — create a company file first.'}
            </p>
          ) : (
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="mt-2" data-testid="agent-other-company-select">
                <SelectValue placeholder={isAr ? 'اختر شركة…' : 'Select a company…'} />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {isAr ? (c.company_name_ar || c.company_name_en) : (c.company_name_en || c.company_name_ar)}
                    {c.license_number ? ` — ${c.license_number}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-4">
          <label className="flex items-center gap-2 px-4 py-2 bg-[#1a1a2e] border border-[#2a2a4e] rounded-xl cursor-pointer hover:bg-[#2a2a4e]">
            <Upload className="h-4 w-4 text-white" />
            <span className="text-sm text-white">
              {file ? file.name : (isAr ? 'اختر الملف' : 'Choose file')}
            </span>
            <input type="file" accept="image/*,application/pdf" className="hidden"
                   onChange={handleFilePick} data-testid="agent-other-file-input" />
          </label>
          <Button
            onClick={runAIExtract}
            disabled={!file || extracting}
            className="bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl"
            data-testid="agent-other-extract-btn"
          >
            {extracting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Sparkles className="h-4 w-4 me-2" />}
            {isAr ? 'اقرأ العنوان بالذكاء الاصطناعي' : 'Read title with AI'}
          </Button>
        </div>

        <div>
          <Label>{isAr ? 'عناوين الوثيقة (عربي / إنجليزي)' : 'Document titles (AR / EN)'}</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-white/60">{isAr ? 'العنوان بالعربية' : 'Arabic title'}</span>
                <button
                  onClick={() => autoTranslate('en_to_ar')}
                  disabled={translating || !titleEn.trim()}
                  className="text-[10px] px-2 py-0.5 rounded-md bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 flex items-center gap-1 disabled:opacity-40"
                  data-testid="agent-other-translate-en-to-ar"
                >
                  {translating ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Sparkles className="h-2.5 w-2.5" />}
                  {isAr ? 'ترجم من الإنجليزي' : 'Translate from EN'}
                </button>
              </div>
              <Input
                value={titleAr}
                onChange={(e) => setTitleAr(e.target.value)}
                placeholder={isAr ? 'عنوان بالعربية' : 'عنوان بالعربية'}
                dir="rtl"
                data-testid="agent-other-title-ar-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-white/60">{isAr ? 'العنوان بالإنجليزية' : 'English title'}</span>
                <button
                  onClick={() => autoTranslate('ar_to_en')}
                  disabled={translating || !titleAr.trim()}
                  className="text-[10px] px-2 py-0.5 rounded-md bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 flex items-center gap-1 disabled:opacity-40"
                  data-testid="agent-other-translate-ar-to-en"
                >
                  {translating ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Sparkles className="h-2.5 w-2.5" />}
                  {isAr ? 'ترجم من العربي' : 'Translate from AR'}
                </button>
              </div>
              <Input
                value={titleEn}
                onChange={(e) => setTitleEn(e.target.value)}
                placeholder="English title"
                dir="ltr"
                data-testid="agent-other-title-en-input"
              />
            </div>
          </div>
          <p className="text-[10px] text-white/40 mt-1">
            {isAr
              ? 'اكتب أي حقل ثم اضغط "ترجم" لملء الآخر تلقائياً بالذكاء الاصطناعي.'
              : 'Fill either field, then click Translate to auto-fill the other via AI.'}
          </p>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            onClick={handleSave}
            disabled={saving || !file || (!titleAr.trim() && !titleEn.trim()) || !companyId}
            className="bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl"
            data-testid="agent-other-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Save className="h-4 w-4 me-2" />}
            {isAr ? 'حفظ الوثيقة' : 'Save document'}
          </Button>
        </div>
      </motion.section>

      {/* iter-262 — The saved documents are now displayed INSIDE the parent
         company file (with its own search). This page is a pure insert
         surface, so we intentionally do NOT render a list here. */}
    </AgentPageShell>
  );
};

export default AgentOtherDocsPage;
